import React from 'react';
import { MONTH_NAMES, getWeekNumber, isSameDay, isAllDay, formatAssignmentDateSpan } from '../../../../../utils/dateUtils';
import AssignmentChip from './AssignmentChip';
import './MonthGrid.css';

/**
 * Grid rendering logic for the Month View.
 * Displays calendar weeks, day names, and truncates assignment labels per day.
 * Supports drag and drop movement of assignments.
 * 
 * @param {Object} props
 * @param {Date} props.startDate - Active month context date
 * @param {Array<Object>} props.assignments - All assignments mapped to date range
 * @param {Date} props.today - Today's reference date
 * @param {Function} props.onDayClick - Handler for day cell clicks
 * @param {Function} props.onAssignmentClick - Handler for assignment clicks
 * @param {Object} props.assignmentConflictsMap - Lookup for assignments with conflicts
 * @param {Function} props.onContextMenu - Handler for context menu
 * @param {Function} props.handleMonthDragEnd - Handler for drag end events
 * @param {Object} props.interaction - Current interaction/drag state
 * @param {boolean} props.isConflictDetectionEnabled - Whether to show red icons
 * @param {boolean} props.isReadOnly - Whether interactions are restricted (Rank 2)
 */
export function MonthGrid({
    startDate,
    assignments,
    today,
    onDayClick,
    onAssignmentClick,
    onAssignmentHover,
    onAssignmentHoverEnd,
    onConflictHover,
    onConflictHoverEnd,
    assignmentConflictsMap,
    onContextMenu,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleMonthDragStart,
    handleMonthDragEnd,
    interaction,
    monthDragPreview,
    isConflictDetectionEnabled = true,
    isReadOnly = false
}) {
    return (
        <div className="calendar-month-grid">
            {/* Day name headers */}
            <div className="month-header-row">
                <div className="month-header-cell week-num">KW</div>
                {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(d => (
                    <div key={d} className="month-header-cell">{d}</div>
                ))}
            </div>
            {/* Build weeks */}
            {(() => {
                const year = startDate.getFullYear();
                const month = startDate.getMonth();
                const firstOfMonth = new Date(year, month, 1);
                const firstDayOfWeek = (firstOfMonth.getDay() + 6) % 7; // Mo=0
                const prevMonthLastDay = new Date(year, month, 0).getDate();
                const daysInMonth = new Date(year, month + 1, 0).getDate();
                const weeks = [];
                
                // Trailing days from previous month
                let week = [];
                for (let i = firstDayOfWeek - 1; i >= 0; i--) {
                    week.push(new Date(year, month - 1, prevMonthLastDay - i));
                }

                // Days of current month
                for (let d = 1; d <= daysInMonth; d++) {
                    week.push(new Date(year, month, d));
                    if (week.length === 7) {
                        weeks.push(week);
                        week = [];
                    }
                }

                // Leading days of next month
                if (week.length > 0) {
                    let nextDay = 1;
                    while (week.length < 7) {
                        week.push(new Date(year, month + 1, nextDay++));
                    }
                    weeks.push(week);
                }

                return weeks.map((w, wi) => {
                    const kw = getWeekNumber(w[0]);
                    
                    return (
                        <div key={wi} className="month-week-row">
                            <div className="month-kw-cell">{kw}</div>
                            {w.map((day, di) => {
                                const isCurrentMonth = day.getMonth() === month;
                                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                                const isT = isSameDay(day, today);
                                
                                const dayAssignments = (assignments || []).filter(a => {
                                    if (!a.start_datetime) return false;
                                    if (interaction?.type === 'month-move' && interaction.assignment && String(a.id) === String(interaction.assignment.id)) {
                                        return false;
                                    }
                                    const aS = new Date(a.start_datetime);
                                    const aE = new Date(a.end_datetime || a.start_datetime);
                                    aS.setHours(0, 0, 0, 0);
                                    aE.setHours(0, 0, 0, 0);
                                    return day >= aS && day <= aE;
                                });

                                // Check if this day is being hovered by an assignment drag
                                const isHovered = interaction?.type === 'month-move' && 
                                                interaction.targetDay && 
                                                isSameDay(interaction.targetDay, day);
                                const previewAssignments = (monthDragPreview?.previewAssignments || []).filter(a => {
                                    const previewDay = new Date(a.start_datetime);
                                    previewDay.setHours(0, 0, 0, 0);
                                    return isSameDay(previewDay, day);
                                });

                                return (
                                    <div
                                        key={di}
                                        className={`month-cell ${!isCurrentMonth ? 'not-current-month' : ''} ${isWeekend ? 'weekend' : ''} ${isT ? 'today' : ''} ${isHovered ? 'drag-over' : ''} ${isReadOnly ? 'read-only' : ''}`}
                                        onClick={() => !isReadOnly && onDayClick && onDayClick(day)}
                                        onContextMenu={(e) => !isReadOnly && onContextMenu && onContextMenu(e, null, day)}
                                        onDrop={(e) => !isReadOnly && handleDrop && handleDrop(e, day)}
                                        onDragOver={(e) => !isReadOnly && handleDragOver && handleDragOver(e, day)}
                                        onDragLeave={!isReadOnly ? handleDragLeave : undefined}
                                    >
                                        <div className="month-cell-header">
                                            <div className="month-cell-date">{day.getDate()}</div>
                                            {!isCurrentMonth && (
                                                <div className="month-cell-month-label">
                                                    {MONTH_NAMES[day.getMonth()].substring(0, 3)}
                                                </div>
                                            )}
                                        </div>
                                        {dayAssignments.slice(0, 3).map(a => {
                                            const isDragging = interaction?.type === 'month-move' && 
                                                               String(interaction.assignment.id) === String(a.id) &&
                                                               interaction.moved;
                                            
                                            return (
                                                <AssignmentChip
                                                    key={a.id}
                                                    assignment={a}
                                                    mode="month"
                                                    hasConflict={assignmentConflictsMap[a.id]?.hasConflict}
                                                    isConflictDetectionEnabled={isConflictDetectionEnabled}
                                                    interaction={interaction}
                                                    onDrop={handleDrop}
                                                    style={isDragging ? { opacity: 0, pointerEvents: 'none' } : {}}
                                                    onClick={(e) => { e.stopPropagation(); onAssignmentClick(a); }}
                                                    onMouseEnter={(e) => onAssignmentHover?.(a, e)}
                                                    onMouseLeave={onAssignmentHoverEnd}
                                                    onMouseDown={(e) => {
                                                        if (isReadOnly) return;
                                                        onAssignmentHoverEnd?.();
                                                        handleMonthDragStart?.(a, day);
                                                    }}
                                                    onContextMenu={(e) => { e.stopPropagation(); if (!isReadOnly) onContextMenu?.(e, a); }}
                                                    onDragStart={(e) => {
                                                        if (isReadOnly) { e.preventDefault(); return; }
                                                        const dragSourceEl = e.currentTarget;
                                                        handleMonthDragStart?.(a, day);
                                                        e.dataTransfer.effectAllowed = 'move';
                                                        const transparentPixel = new Image();
                                                        transparentPixel.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACw=';
                                                        e.dataTransfer.setDragImage(transparentPixel, 0, 0);
                                                        window.setTimeout(() => {
                                                            dragSourceEl?.classList.add('is-month-dragging');
                                                        }, 0);
                                                        e.dataTransfer.setData('application/json', JSON.stringify({ 
                                                            type: 'assignment', 
                                                            assignment: a,
                                                            originDay: day.toISOString()
                                                        }));
                                                    }}
                                                    onDragEnd={(e) => {
                                                        handleMonthDragEnd?.();
                                                        e.currentTarget.classList.remove('is-month-dragging');
                                                    }}
                                                />
                                            );
                                        })}
                                        {previewAssignments.map(a => (
                                            <AssignmentChip
                                                key={a.id}
                                                assignment={a}
                                                mode="month"
                                                isGhost={true}
                                                hasConflict={false}
                                                isConflictDetectionEnabled={false}
                                            />
                                        ))}
                                        {dayAssignments.length > 3 && (
                                            <div className="month-event-more">+{dayAssignments.length - 3}</div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    );
                });
            })()}
            {interaction?.type === 'month-move' && interaction?.currentX != null && interaction?.currentY != null && interaction?.assignment && (
                <div
                    className="month-drag-ghost"
                    style={{
                        position: 'fixed',
                        left: interaction.currentX + 12,
                        top: interaction.currentY + 12,
                        pointerEvents: 'none',
                        zIndex: 9999
                    }}
                >
                    <AssignmentChip
                        assignment={interaction.assignment}
                        mode="month"
                        isGhost={true}
                        hasConflict={false}
                        isConflictDetectionEnabled={false}
                        style={{ position: 'relative' }}
                    />
                </div>
            )}
        </div>
    );
}
